import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, MessageCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { contactInfo, submitContactForm, submitBookingForm } from '../mock';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage } from '../LanguageContext';
import { toast } from 'sonner';
import '../styles/harvest.css';

const Contact = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [activeForm, setActiveForm] = useState('contact');
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [bookingFormData, setBookingFormData] = useState({
    parentName: '',
    email: '',
    phone: '',
    childName: '',
    childAge: '',
    concerns: '',
    preferredDate: '',
    preferredTime: ''
  });
  const [loading, setLoading] = useState(false);

  // Handle URL hash to auto-switch to booking form
  useEffect(() => {
    if (location.hash === '#book-consultation') {
      setActiveForm('booking');
      // Scroll to the form section
      setTimeout(() => {
        const formSection = document.getElementById('book-consultation');
        if (formSection) {
          formSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await submitContactForm(contactFormData);
      toast.success(language === 'ar' ? 'تم إرسال رسالتك بنجاح!' : result.message);
      setContactFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ. يرجى المحاولة مرة أخرى.' : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await submitBookingForm(bookingFormData);
      toast.success(language === 'ar' ? 'تم استلام طلبك! سنتواصل معك قريباً.' : result.message);
      setBookingFormData({
        parentName: '',
        email: '',
        phone: '',
        childName: '',
        childAge: '',
        concerns: '',
        preferredDate: '',
        preferredTime: ''
      });
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ. يرجى المحاولة مرة أخرى.' : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fade-in">
      {/* Hero Section */}
      <section className="section" style={{ paddingTop: '6rem', background: 'var(--gradient-hero-subtle)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="heading-hero mb-4">{t('contact.title')}</h1>
            <p className="body-large">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods Section */}
      <section className="section">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            {/* Quick Contact Options */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg p-6 text-center hover-lift">
                <div className="flex justify-center mb-3">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                    <Phone size={28} className="text-gray-700" />
                  </div>
                </div>
                <h3 className="heading-3 mb-2">{t('contact.callUs')}</h3>
                <a href={`tel:${contactInfo.phone}`} className="body-medium" style={{ color: 'var(--text-primary)' }} dir="ltr">
                  {contactInfo.phone}
                </a>
                <p className="caption mt-1">{language === 'ar' ? 'متاح خلال ساعات العمل' : 'Available during operating hours'}</p>
              </div>

              <div className="bg-white rounded-lg p-6 text-center hover-lift">
                <div className="flex justify-center mb-3">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                    <MessageCircle size={28} className="text-gray-700" />
                  </div>
                </div>
                <h3 className="heading-3 mb-2">{t('contact.whatsappTitle')}</h3>
                <p className="body-medium mb-3">{language === 'ar' ? 'ردود سريعة' : 'Quick responses'}</p>
                <WhatsAppButton className="btn-secondary" />
              </div>

              <div className="bg-white rounded-lg p-6 text-center hover-lift">
                <div className="flex justify-center mb-3">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                    <Mail size={28} className="text-gray-700" />
                  </div>
                </div>
                <h3 className="heading-3 mb-2">{t('contact.emailUs')}</h3>
                <a href={`mailto:${contactInfo.email}`} className="body-small" style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                  {contactInfo.email}
                </a>
                <p className="caption mt-1">{language === 'ar' ? 'نرد خلال 24 ساعة' : 'We respond within 24 hours'}</p>
              </div>
            </div>

            {/* Contact Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white rounded-lg p-6">
                <div className={`flex items-start gap-3 ${language === 'ar' ? 'flex-row-reverse text-right' : ''}`}>
                  <MapPin size={24} className="text-gray-700 mt-1" />
                  <div>
                    <h3 className="heading-3 mb-2">{t('contact.visitUs')}</h3>
                    <p className="body-medium">{contactInfo.address}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <div className={`flex items-start gap-3 ${language === 'ar' ? 'flex-row-reverse text-right' : ''}`}>
                  <Clock size={24} className="text-gray-700 mt-1" />
                  <div>
                    <h3 className="heading-3 mb-2">{t('contact.workingHours')}</h3>
                    <p className="body-medium" dir="ltr">{language === 'ar' ? 'السبت - الأربعاء: 10:00 ص - 4:00 م' : contactInfo.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Tabs */}
            <div id="book-consultation" className="bg-white rounded-lg p-8 md:p-12" style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
              <div className="flex gap-4 mb-6 border-b border-gray-200">
                <button
                  onClick={() => setActiveForm('contact')}
                  className={`pb-3 px-4 font-mono text-sm uppercase tracking-wide transition-colors ${
                    activeForm === 'contact'
                      ? 'border-b-2 border-gray-900 font-semibold'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {language === 'ar' ? 'استفسار عام' : 'General Inquiry'}
                </button>
                <button
                  onClick={() => setActiveForm('booking')}
                  className={`pb-3 px-4 font-mono text-sm uppercase tracking-wide transition-colors ${
                    activeForm === 'booking'
                      ? 'border-b-2 border-gray-900 font-semibold'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {language === 'ar' ? 'حجز استشارة' : 'Book Consultation'}
                </button>
              </div>

              {/* Contact Form */}
              {activeForm === 'contact' && (
                <form onSubmit={handleContactSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="form-group">
                      <label htmlFor="name" className="form-label">{t('contact.yourName')} *</label>
                      <input
                        type="text"
                        id="name"
                        className="form-input"
                        value={contactFormData.name}
                        onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email" className="form-label">{t('contact.yourEmail')} *</label>
                      <input
                        type="email"
                        id="email"
                        className="form-input"
                        value={contactFormData.email}
                        onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">{t('contact.phone')}</label>
                    <input
                      type="tel"
                      id="phone"
                      className="form-input"
                      value={contactFormData.phone}
                      onChange={(e) => setContactFormData({ ...contactFormData, phone: e.target.value })}
                      dir="ltr"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="message" className="form-label">{t('contact.message')} *</label>
                    <textarea
                      id="message"
                      className="form-textarea"
                      value={contactFormData.message}
                      onChange={(e) => setContactFormData({ ...contactFormData, message: e.target.value })}
                      required
                    />
                  </div>
                  <button type="submit" className="btn-primary w-full md:w-auto" disabled={loading}>
                    {loading ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : t('contact.sendMessage')}
                  </button>
                </form>
              )}

              {/* Booking Form */}
              {activeForm === 'booking' && (
                <form onSubmit={handleBookingSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="form-group">
                      <label htmlFor="parentName" className="form-label">{language === 'ar' ? 'اسم ولي الأمر' : 'Parent/Guardian Name'} *</label>
                      <input
                        type="text"
                        id="parentName"
                        className="form-input"
                        value={bookingFormData.parentName}
                        onChange={(e) => setBookingFormData({ ...bookingFormData, parentName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="bookingEmail" className="form-label">{t('contact.yourEmail')} *</label>
                      <input
                        type="email"
                        id="bookingEmail"
                        className="form-input"
                        value={bookingFormData.email}
                        onChange={(e) => setBookingFormData({ ...bookingFormData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="form-group">
                      <label htmlFor="bookingPhone" className="form-label">{t('contact.phone')} *</label>
                      <input
                        type="tel"
                        id="bookingPhone"
                        className="form-input"
                        value={bookingFormData.phone}
                        onChange={(e) => setBookingFormData({ ...bookingFormData, phone: e.target.value })}
                        required
                        dir="ltr"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="childName" className="form-label">{language === 'ar' ? 'اسم الطفل' : "Child's Name"} *</label>
                      <input
                        type="text"
                        id="childName"
                        className="form-input"
                        value={bookingFormData.childName}
                        onChange={(e) => setBookingFormData({ ...bookingFormData, childName: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="childAge" className="form-label">{language === 'ar' ? 'عمر الطفل' : "Child's Age"} *</label>
                    <select
                      id="childAge"
                      className="form-select"
                      value={bookingFormData.childAge}
                      onChange={(e) => setBookingFormData({ ...bookingFormData, childAge: e.target.value })}
                      required
                    >
                      <option value="">{language === 'ar' ? 'اختر العمر' : 'Select age'}</option>
                      {[3,4,5,6,7,8,9,10].map(age => (
                        <option key={age} value={age}>{language === 'ar' ? `${age} سنوات` : `${age} years old`}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="concerns" className="form-label">{language === 'ar' ? 'المخاوف الرئيسية / مجالات الدعم المطلوبة' : 'Main Concerns/Areas of Support Needed'} *</label>
                    <textarea
                      id="concerns"
                      className="form-textarea"
                      placeholder={language === 'ar' ? 'مثل: تأخر النطق، صعوبة التركيز، المهارات الاجتماعية، إلخ.' : 'E.g., speech delay, trouble focusing, social skills, etc.'}
                      value={bookingFormData.concerns}
                      onChange={(e) => setBookingFormData({ ...bookingFormData, concerns: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="form-group">
                      <label htmlFor="preferredDate" className="form-label">{language === 'ar' ? 'التاريخ المفضل' : 'Preferred Date'}</label>
                      <input
                        type="date"
                        id="preferredDate"
                        className="form-input"
                        value={bookingFormData.preferredDate}
                        onChange={(e) => setBookingFormData({ ...bookingFormData, preferredDate: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="preferredTime" className="form-label">{language === 'ar' ? 'الوقت المفضل' : 'Preferred Time'}</label>
                      <select
                        id="preferredTime"
                        className="form-select"
                        value={bookingFormData.preferredTime}
                        onChange={(e) => setBookingFormData({ ...bookingFormData, preferredTime: e.target.value })}
                      >
                        <option value="">{language === 'ar' ? 'اختر الوقت' : 'Select time'}</option>
                        <option value="10:00 AM">10:00 AM</option>
                        <option value="11:00 AM">11:00 AM</option>
                        <option value="12:00 PM">12:00 PM</option>
                        <option value="1:00 PM">1:00 PM</option>
                        <option value="2:00 PM">2:00 PM</option>
                        <option value="3:00 PM">3:00 PM</option>
                      </select>
                    </div>
                  </div>
                  <p className="caption mb-4">{language === 'ar' ? '* سنتواصل معك لتأكيد موعدك' : '* We will contact you to confirm your appointment time'}</p>
                  <button type="submit" className="btn-primary w-full md:w-auto" disabled={loading}>
                    {loading ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') : (language === 'ar' ? 'طلب استشارة' : 'Request Consultation')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
